<template>
  <div class="column alignCentre fullWidth">
    <!-- Drag & Drop and Upload Button Area -->
    <div
      class="drop-area column alignCentre"
      @click="triggerFileInput"
      @dragover.prevent
      @dragenter.prevent="handleDragEnter"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <svg class="smaller">
        <use href="@/assets/icons/iconset.svg#upload"></use>
      </svg>
      <div class="alignCentre">
        <p class="nomargintopbottom">Drag your files here or click to upload</p>
        <div class="or">or</div>
        <UnitButton
          variant="secondary"
          size="small"
          type="button"
          @click.stop="triggerCameraInput"
          v-haptic
        >
          <svg class="compactImg">
            <use href="@/assets/icons/iconset.svg#camera_compact"></use>
          </svg>
          Capture image
        </UnitButton>
      </div>
    </div>

    <!-- Preview Area (reused inline tray) -->
    <InlineMediaTray
      v-if="imagePreviews.length > 0"
      class="previewArea"
      :items="normalizedPreviews"
      :selectedIndex="selectedPreviewIndex"
      :thumbnailFirst="true"
      @select="onTraySelect"
      @remove="onTrayRemove"
      @edit="onTrayEdit"
      @reorder="onTrayReorder"
    />

    <!-- Cropper -->
    <Overlay
      v-if="isCropping"
      :visible="isCropping"
      :title="'Crop image'"
      @close="closeOverlay"
    >
      <template #before-buttons>
        <ImageCrop
          v-if="currentImageIndex !== null"
          :imageSrc="currentImageSrc"
          :currentIndex="currentImageIndex"
          @updateImage="
            (newImageData) => updateImage(newImageData, currentImageIndex)
          "
          @cancelCrop="isCropping = false"
        />
      </template>
    </Overlay>

    <!-- Hidden Inputs -->
    <input
      type="file"
      :accept="acceptedTypes"
      multiple
      @change="handleFileSelection"
      ref="fileInput"
      style="display: none"
    />
    <input
      type="file"
      accept="image/*"
      multiple
      capture="environment"
      @change="handleFileSelection"
      ref="cameraInput"
      style="display: none"
    />
  </div>
</template>

<script>
import { ref, watch, computed, nextTick } from "vue";

import ImageCrop from "@/components/GenericComponents/ImageCrop.vue";
import Overlay from "@/components/GenericComponents/Overlay.vue";
import InlineMediaTray from "@/components/GenericComponents/InlineMediaTray.vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";

export default {
  name: "ImageUpload",
  components: {
    Overlay,
    ImageCrop,
    InlineMediaTray,
    UnitButton,
  },
  props: {
    existingImages: {
      type: Array,
      default: () => [],
    },
    newImages: {
      type: Array,
      default: () => [],
    },
    thumbnailIndex: {
      type: Number,
      default: 0,
    },
    // Allow parent to control accepted file types (e.g., include documents)
    acceptedTypes: {
      type: String,
      default: "image/*",
    },
  },
  emits: ["updateImages", "updateThumbnail"],

  setup(props, { emit }) {
    const store = useStore();
    const fileInput = ref(null);
    const cameraInput = ref(null);
    const existingImages = ref([...props.existingImages]);
    const selectedFiles = ref([...props.newImages]);
    // Maintain a user-defined order of originalIndexes (excluding the current thumbnail-first transform)
    const userOrder = ref([]); // array of originalIndex reflecting tray order (excluding forced thumbnail-first view)
    const localThumbnailIndex = ref(props.thumbnailIndex);
    const editedImages = ref([]); // Stores objects { oldUrl, newFile }
    const removedImageUrls = ref([]); // Stores URLs of images to delete
    const editedImageUrls = ref(new Map()); // Map from oldUrl to blobUrl

    watch(
      () => props.existingImages,
      (newVal) => {
        existingImages.value = [...newVal];
      }
    );

    watch(
      () => props.newImages,
      (newVal) => {
        if (Array.isArray(newVal)) {
          selectedFiles.value = [...newVal];
        }
      },
      { immediate: true }
    );

    watch(
      () => props.thumbnailIndex,
      (newIndex) => {
        localThumbnailIndex.value =
          newIndex === null || newIndex === undefined ? null : Number(newIndex);
      }
    );

    const fileToUrlMap = new Map();
    const imagePreviews = computed(() => {
      // Use Sets to track URLs and prevent duplicates
      const urlSet = new Set();
      const uniqueImages = [];

      // Process existing images
      existingImages.value.forEach((item, idx) => {
        let displayUrl;
        if (typeof item === "string") {
          // It's a URL string
          displayUrl = editedImageUrls.value.get(item) || item;
        } else if (item && item.url && typeof item.url === "string") {
          // It's an object with URL property
          displayUrl = editedImageUrls.value.get(item.url) || item.url;
        } else {
          console.warn(
            "[ImageUpload] Unknown format for existing image:",
            item
          );
          displayUrl = ""; // Default empty URL
        }

        // Skip blob preview if we also have new files; avoids duplicate of same image
        if (
          displayUrl &&
          typeof displayUrl === "string" &&
          displayUrl.startsWith("blob:") &&
          selectedFiles.value &&
          selectedFiles.value.length > 0
        ) {
          return;
        }

        // Only add if this URL hasn't been seen before
        if (displayUrl && !urlSet.has(displayUrl)) {
          urlSet.add(displayUrl);
          uniqueImages.push({ src: displayUrl, originalIndex: idx });
        }
      });

      // Process new files (creating blob URLs as needed)
      selectedFiles.value.forEach((file, idx) => {
        const originalIndex = idx + existingImages.value.length;
        let src = "";

        if (file instanceof File) {
          // For File objects, create and cache object URLs
          if (!fileToUrlMap.has(file)) {
            const objectUrl = URL.createObjectURL(file);
            fileToUrlMap.set(file, objectUrl);
          }
          src = fileToUrlMap.get(file);
        } else if (typeof file === "string") {
          // For string URLs, use them directly
          src = file;
        } else if (file && file.url && typeof file.url === "string") {
          // For file-like objects with URL properties
          src = file.url;
        } else {
          console.warn("[ImageUpload] Unknown format for selected file:", file);
          return; // Skip invalid entries
        }

        // Only add if this URL hasn't been seen before
        if (src && !urlSet.has(src)) {
          urlSet.add(src);
          uniqueImages.push({ src, originalIndex });
        }
      });

      // Create a mutable array from uniqueImages
      let processedImages = [...uniqueImages]; // Start with deduplicated images

      // Check if localThumbnailIndex.value is valid
      if (
        localThumbnailIndex.value === null ||
        localThumbnailIndex.value >= processedImages.length
      ) {
        if (processedImages.length > 0) {
          localThumbnailIndex.value = 0;
          emit("updateThumbnail", 0);
        } else {
          localThumbnailIndex.value = null;
          emit("updateThumbnail", null);
        }
      }

      // IMPORTANT: Sort the images to put the thumbnail first for display
      if (localThumbnailIndex.value !== null) {
        // Find the image with the thumbnail index
        const thumbnailIdx = processedImages.findIndex(
          (img) => img.originalIndex === localThumbnailIndex.value
        );

        if (thumbnailIdx > 0) {
          // Move the thumbnail image to the front of the array
          const thumbnailImage = processedImages.splice(thumbnailIdx, 1)[0];
          processedImages.unshift(thumbnailImage);
        }
      }

      // For debugging purposes, log the full state
      console.log(
        "[ImageUpload] Sorted images with thumbnail first:",
        JSON.stringify(
          processedImages.map((img) => ({
            originalIndex: img.originalIndex,
            isThumbnail: img.originalIndex === localThumbnailIndex.value,
            src: img.src.substring(0, 30) + "...", // truncate for readability
          }))
        )
      );

      // Return the final processed array
      // Apply userOrder if present (this preserves drag-and-drop order after the thumbnail)
      if (userOrder.value && userOrder.value.length) {
        const thumbIdx = processedImages.findIndex(
          (img) => img.originalIndex === localThumbnailIndex.value
        );
        const thumbnailImage =
          thumbIdx >= 0 ? processedImages.splice(thumbIdx, 1)[0] : null;
        const orderSet = new Set(userOrder.value);
        const orderedRest = processedImages.slice().sort((a, b) => {
          const ai = userOrder.value.indexOf(a.originalIndex);
          const bi = userOrder.value.indexOf(b.originalIndex);
          if (ai === -1 && bi === -1) return 0;
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        });
        const finalList = [];
        if (thumbnailImage) finalList.push(thumbnailImage);
        finalList.push(...orderedRest);
        return finalList;
      }
      return processedImages;
    });

    // Normalize for InlineMediaTray: [{ src, originalIndex }]
    const normalizedPreviews = computed(() =>
      (imagePreviews.value || []).map((img) => ({
        src: img.src,
        originalIndex: img.originalIndex,
      }))
    );

    const selectedPreviewIndex = computed(() => {
      if (!imagePreviews.value || imagePreviews.value.length === 0) return 0;
      const idx = imagePreviews.value.findIndex(
        (img) => img.originalIndex === localThumbnailIndex.value
      );
      return idx >= 0 ? idx : 0;
    });

    const thumbnailImage = computed(() => {
      try {
        const list = imagePreviews.value || [];
        if (!list.length) return null;
        // Prefer the item matching the selected originalIndex
        if (
          localThumbnailIndex.value !== null &&
          localThumbnailIndex.value !== undefined
        ) {
          const match = list.find(
            (img) => img.originalIndex === Number(localThumbnailIndex.value)
          );
          if (match) return match;
        }
        // Fallback to the first image
        return list[0];
      } catch (err) {
        console.error("[ImageUpload] Error getting thumbnailImage:", err);
        return null;
      }
    });

    const otherImages = computed(() => {
      try {
        if (!imagePreviews.value || imagePreviews.value.length === 0) {
          return [];
        }
        const thumb = thumbnailImage.value;
        // Exclude any image whose src or originalIndex matches the thumbnail
        return imagePreviews.value.filter((img, idx) => {
          if (!thumb) return idx > 0; // fallback to slice behavior
          const sameSrc = img.src === thumb.src;
          const sameIdx = img.originalIndex === thumb.originalIndex;
          return !sameSrc && !sameIdx;
        });
      } catch (err) {
        console.error("[ImageUpload] Error getting otherImages:", err);
        return [];
      }
    });

    // Watch to ensure localThumbnailIndex remains valid
    watch(
      () => existingImages.value.length + selectedFiles.value.length,
      (newTotal) => {
        if (
          localThumbnailIndex.value !== null &&
          localThumbnailIndex.value >= newTotal
        ) {
          localThumbnailIndex.value = newTotal > 0 ? newTotal - 1 : null;
          emit("updateThumbnail", localThumbnailIndex.value);
        }
      }
    );

    const isCropping = ref(false);
    const currentImageSrc = ref(null);
    const currentImageIndex = ref(null);

    const setThumbnail = (originalIndex) => {
      try {
        console.log(
          "[ImageUpload] Setting thumbnail to originalIndex:",
          originalIndex
        );

        // Find the actual image
        const selectedImage = imagePreviews.value.find(
          (img) => img.originalIndex === originalIndex
        );

        if (selectedImage) {
          // Just update the index - no need to manually reorder arrays here
          localThumbnailIndex.value = Number(originalIndex);

          // Important: emit and update immediately to ensure parent gets updated
          emitUpdateImages();

          console.log("[ImageUpload] Thumbnail set to:", originalIndex);

          // Force UI update
          nextTick(() => {
            console.log("[ImageUpload] UI updated after thumbnail change");
          });
        } else {
          console.error(
            "[ImageUpload] Could not find image with index:",
            originalIndex
          );
        }
      } catch (err) {
        console.error("[ImageUpload] Error setting thumbnail:", err);
      }
    };

    // Inline tray handlers
    const onTraySelect = (i) => {
      const img = imagePreviews.value?.[i];
      if (!img) return;
      setThumbnail(img.originalIndex);
    };
    const onTrayRemove = (i) => {
      const img = imagePreviews.value?.[i];
      if (!img) return;
      removeImage(img.originalIndex);
    };
    const onTrayEdit = (i) => {
      const img = imagePreviews.value?.[i];
      if (!img) return;
      editImage(img.originalIndex);
    };

    const onTrayReorder = ({ from, to }) => {
      // Rebuild userOrder based on current imagePreviews order after moving from->to (excluding the thumbnail-first transform)
      const current = imagePreviews.value.map((img) => img.originalIndex);
      // Ensure we treat current[0] as thumbnail; only reorder within the rest segment
      const startIdx = 1; // index 0 is thumbnail
      const within = current.slice(startIdx);
      const relFrom = Math.max(0, from - startIdx);
      const relTo = Math.max(0, to - startIdx);
      if (from === 0 || to === 0) {
        // If user tries to drag to/from the thumbnail position, just set thumbnail
        const movedIdx = current[from];
        if (typeof movedIdx === "number") {
          setThumbnail(movedIdx);
        }
        return;
      }
      if (
        relFrom >= 0 &&
        relFrom < within.length &&
        relTo >= 0 &&
        relTo < within.length
      ) {
        const [moved] = within.splice(relFrom, 1);
        within.splice(relTo, 0, moved);
        userOrder.value = within;
        emitUpdateImages();
      }
    };

    const closeOverlay = async () => {
      document.body.classList.remove("no-scroll");
      isCropping.value = false;
      store.dispatch("toggleNavbarVisibility", true); // Show the Navbar
    };

    const triggerFileInput = () => {
      fileInput.value.click();
    };

    const triggerCameraInput = () => {
      cameraInput.value.click();
    };

    const handleFileSelection = async (event) => {
      const newFiles = event.target.files ? Array.from(event.target.files) : [];
      const existingFileNames = new Set(
        selectedFiles.value.map((file) => file.name)
      );
      const uniqueNewFiles = newFiles.filter(
        (file) => !existingFileNames.has(file.name)
      );

      console.log("New files selected:", newFiles);
      console.log("Unique new files to be added:", uniqueNewFiles);

      const processedFiles = await Promise.all(
        uniqueNewFiles.map((file) => processImageFile(file))
      );

      selectedFiles.value = [...selectedFiles.value, ...processedFiles];
      emitUpdateImages();
      event.target.value = "";
    };

    const processImageFile = (file) => {
      return new Promise((resolve, reject) => {
        if (
          !(
            file &&
            typeof file.type === "string" &&
            file.type.startsWith("image/")
          )
        ) {
          resolve(file);
          return;
        }
        // If the file is an animated GIF, do not process it via canvas
        if (file.type === "image/gif") {
          console.log(
            "Animated GIF detected - skipping compression to preserve animation"
          );
          resolve(file);
          return;
        }
        const maxWidth = 1920;
        const maxHeight = 1920;
        const compressionQuality = 1.0; // 100% quality - no JPEG compression
        const reader = new FileReader();

        reader.onload = (e) => {
          const img = new Image();
          img.src = e.target.result;
          img.onload = () => {
            let { width, height } = img;

            // Calculate size in MB before compression
            const originalSize = file.size / (1024 * 1024);
            console.log(`Original image size: ${originalSize.toFixed(2)} MB`);

            // Resize if dimensions exceed max limits
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, width, height);

            // For JPEGs and other compressible formats, use quality setting
            const mimeType =
              file.type === "image/png" ? "image/png" : "image/jpeg";
            const quality =
              file.type === "image/png" ? 1.0 : compressionQuality;

            canvas.toBlob(
              (blob) => {
                // Calculate size after compression
                const compressedSize = blob.size / (1024 * 1024);
                console.log(
                  `Compressed image size: ${compressedSize.toFixed(2)} MB`
                );
                console.log(
                  `Compression ratio: ${(
                    (1 - compressedSize / originalSize) *
                    100
                  ).toFixed(0)}%`
                );

                const processedFile = new File([blob], file.name, {
                  type: mimeType,
                });
                resolve(processedFile);
              },
              mimeType,
              quality
            );
          };
        };

        reader.onerror = (err) => {
          reject(err);
        };

        reader.readAsDataURL(file);
      });
    };

    const emitUpdateImages = () => {
      // Build ordered list reflecting the current tray order (thumbnail-first + user reorder)
      const orderedIndexes = (imagePreviews.value || []).map(
        (img) => img.originalIndex
      );
      let orderedImages = orderedIndexes.map((idx) =>
        idx < existingImages.value.length
          ? existingImages.value[idx]
          : selectedFiles.value[idx - existingImages.value.length]
      );

      // Output the full list of images for debugging
      console.log(
        "[ImageUpload] *** FINAL ORDERED IMAGES ***",
        orderedImages.map((img, idx) => {
          let desc;
          if (img instanceof File) {
            desc = `File: ${img.name}`;
          } else if (typeof img === "string") {
            desc = `URL: ${img.substring(0, 20)}...`;
          } else if (img && img.url) {
            desc = `Obj: ${img.url.substring(0, 20)}...`;
          } else {
            desc = `Unknown: ${typeof img}`;
          }
          return `${idx === 0 ? "THUMBNAIL → " : ""}${idx}: ${desc}`;
        })
      );

      // CRITICAL - Always notify parent, even when empty
      if (orderedImages.length > 0) {
        // Keep localThumbnailIndex as the original index that was clicked,
        // so computed(imagePreviews) can bring that image to the front immediately.
        // Inform parent of the chosen original index; parent can reorder for save.
        emit("updateThumbnail", localThumbnailIndex.value);
      } else {
        // No images left, clear thumbnail selection in parent
        localThumbnailIndex.value = null;
        emit("updateThumbnail", null);
      }

      // Get URLs for reference (empty when none)
      const imagePreviewUrls = imagePreviews.value.map((img) => img.src);

      // Finally emit the images array (possibly empty) so parent can clear state
      emit(
        "updateImages",
        orderedImages,
        removedImageUrls.value,
        editedImages.value,
        imagePreviewUrls
      );
    };

    const handleDragEnter = (event) => {
      event.target.classList.add("drag-over");
    };

    const handleDragLeave = (event) => {
      event.target.classList.remove("drag-over");
    };

    const handleDrop = (event) => {
      const newFiles = [...event.dataTransfer.files];
      console.log("Files dropped:", newFiles);
      handleFileSelection({ target: { files: newFiles } });
    };

    const removeImage = (originalIndex) => {
      console.log("Removing image with original index:", originalIndex);

      // Determine if the image is in existingImages or selectedFiles
      if (originalIndex < existingImages.value.length) {
        const removedUrl = existingImages.value.splice(originalIndex, 1)[0];
        removedImageUrls.value.push(removedUrl);
        console.log("Removed URL from existing images:", removedUrl);
      } else {
        const newImageIndex = originalIndex - existingImages.value.length;
        const removedFile = selectedFiles.value.splice(newImageIndex, 1)[0];
        const removedObjectUrl = fileToUrlMap.get(removedFile);
        fileToUrlMap.delete(removedFile);
        URL.revokeObjectURL(removedObjectUrl);
        console.log("Removed file from new images:", removedFile);
      }

      // Adjust localThumbnailIndex if necessary
      const totalImages =
        existingImages.value.length + selectedFiles.value.length;

      if (localThumbnailIndex.value === originalIndex) {
        // The thumbnail image was removed
        if (totalImages > 0) {
          // Set thumbnail to the image at the same position (which shifted into this index)
          if (originalIndex >= totalImages) {
            // If originalIndex is out of bounds, set to last image
            localThumbnailIndex.value = totalImages - 1;
          } else {
            localThumbnailIndex.value = originalIndex;
          }
        } else {
          localThumbnailIndex.value = null; // No images left
        }
        emit("updateThumbnail", localThumbnailIndex.value);
      } else if (originalIndex < localThumbnailIndex.value) {
        // Adjust localThumbnailIndex because the indexes shifted
        localThumbnailIndex.value -= 1;
      }

      emitUpdateImages();
    };

    const editImage = (originalIndex) => {
      document.body.classList.add("no-scroll");
      isCropping.value = true;
      // Find the image src based on originalIndex
      const imageObj = imagePreviews.value.find(
        (img) => img.originalIndex === originalIndex
      );
      if (imageObj) {
        currentImageSrc.value = imageObj.src;
        currentImageIndex.value = originalIndex;
        store.dispatch("toggleNavbarVisibility", false); // Hide the Navbar
        console.log("Editing image at index:", originalIndex);
      } else {
        console.error("Image not found for editing");
      }
    };

    const updateImage = ({ file, url }, originalIndex) => {
      if (originalIndex < existingImages.value.length) {
        const oldUrl = existingImages.value[originalIndex];
        editedImages.value.push({ oldUrl, newFile: file });
        editedImageUrls.value.set(oldUrl, url); // Store blob URL for display
      } else {
        const newImageIndex = originalIndex - existingImages.value.length;
        selectedFiles.value[newImageIndex] = file;
        fileToUrlMap.set(selectedFiles.value[newImageIndex], url);
      }
      emitUpdateImages();
      closeOverlay();
    };

    return {
      fileInput,
      cameraInput,
      triggerFileInput,
      triggerCameraInput,
      handleFileSelection,
      imagePreviews,
      thumbnailImage,
      otherImages,
      normalizedPreviews,
      selectedPreviewIndex,
      onTraySelect,
      onTrayRemove,
      onTrayEdit,
      onTrayReorder,
      handleDragEnter,
      handleDragLeave,
      handleDrop,
      removeImage,
      isCropping,
      editImage,
      updateImage,
      currentImageSrc,
      closeOverlay,
      currentImageIndex,
      localThumbnailIndex,
      setThumbnail,
      removedImageUrls,
      processImageFile,
    };
  },
};
</script>

<style scoped>
.drop-area {
  border: 1px dashed var(--color-border-dropzone);
  padding: var(--space-5);
  text-align: center;
  border-radius: var(--radius-sm);
  width: 100%;
  cursor: pointer;
}

.drop-area.drag-over {
  border-color: var(--color-feedback-success-border-strong);
  background-color: var(--color-feedback-success-surface-strong);
}

/* Preview area styles are now provided by InlineMediaTray */

.image-container {
  position: relative;
}

svg.smallClose {
  position: initial;
}

.previewedImg {
  object-fit: cover;
  width: 100%;
}

.alignCentre {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
</style>
